import { Skeleton } from "../Skeleton";

export interface ResultsCountProps {
  /**
   * Number of results/items. `null` renders `unknownLabel` instead of a number —
   * for a failed query, where showing `0` would assert an empty result set.
   */
  count: number | null;
  /**
   * Label to use (e.g., "results", "items", "sites"). An empty string renders the
   * number alone, for a caller that already names the entity beside the count.
   */
  label?: string;
  /**
   * Appended directly to the number, before the label — e.g. `"+"` for a total that
   * is a lower bound because something upstream truncated the set. Suppresses
   * singular/plural switching, since a suffixed count is never exactly one.
   */
  suffix?: string;
  /** What to show when `count` is null. Defaults to an em-dash. */
  unknownLabel?: string;
  /** Whether the count is loading */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * `data-testid` for the rendered count. The count is the one thing a table's specs
   * reliably need a handle on ("the table published this many rows"), and it is drawn
   * here rather than by the caller.
   */
  testId?: string;
}

/**
 * ResultsCount
 *
 * Displays a count of results or items with proper singular/plural handling.
 * Shows a loading skeleton when data is being fetched.
 *
 * Example usage:
 * ```tsx
 * <ResultsCount count={23} label="results" />
 * // Renders: "23 results"
 *
 * <ResultsCount count={1} label="site" />
 * // Renders: "1 site"
 *
 * <ResultsCount count={42} label="items" isLoading={true} />
 * // Renders: loading skeleton
 * ```
 */
export function ResultsCount({
  count,
  label = "results",
  suffix,
  unknownLabel = "—",
  isLoading = false,
  className = "",
  testId,
}: ResultsCountProps) {
  if (isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <Skeleton width={80} height={16} />
      </div>
    );
  }

  if (count === null) {
    return (
      <div data-testid={testId} className={`text-sm text-text-secondary whitespace-nowrap ${className}`}>
        {unknownLabel}
      </div>
    );
  }

  // Handle singular/plural. A suffixed count is a lower bound ("25,063+"), never
  // exactly one, so it always takes the plural.
  const singularLabel = label.endsWith("s") ? label.slice(0, -1) : label;
  const displayLabel = count === 1 && !suffix ? singularLabel : label;

  // An empty label renders the number alone — for callers that already name the entity
  // adjacent to the count and would otherwise show it twice. Without this the trailing
  // separator space survives into the DOM.
  const formatted = `${count.toLocaleString()}${suffix ?? ""}${displayLabel ? ` ${displayLabel}` : ""}`;

  return (
    <div data-testid={testId} className={`text-sm text-text-secondary whitespace-nowrap ${className}`}>
      {formatted}
    </div>
  );
}
